import type { EstadoReparto } from '../../hooks/useCalendarioMes'

/**
 * Marcador visual del ESTADO de un reparto, separado del color de RUTA.
 *
 * Decisión de diseño (equilibrio pedido): la píldora del calendario combina dos
 * datos —ruta y estado— sin que el color cargue ninguno solo:
 *   - RUTA   → color de FONDO de la píldora (coloresDia.ts, r1..r5).
 *   - ESTADO → este MARCADOR: un símbolo textual + etiqueta + estilo de BORDE.
 *
 * Así un operador que no distinga colores sigue leyendo el estado por el símbolo
 * (✓ · ⏳ · ») y la etiqueta accesible, misma regla que el resto del panel.
 */

export interface MarcaEstado {
  clave: EstadoReparto
  /** Palabra para el humano y el lector de pantalla. Nunca va el símbolo solo. */
  etiqueta: string
  /** Símbolo corto que cabe en la píldora. Refuerza, no sustituye, la etiqueta. */
  simbolo: string
  /**
   * Estilo del BORDE de la píldora según el estado (sobre el fondo de la ruta):
   *   confirmado → borde sólido y marcado (cerrado, firme).
   *   pendiente  → borde punteado (aún abierto).
   *   aplazado   → borde discontinuo tenue (movido a otra fecha).
   * Solo clases de borde: no tocan el fondo (que es de la ruta).
   */
  borde: string
}

const MARCAS: Record<EstadoReparto, MarcaEstado> = {
  confirmado: {
    clave: 'confirmado',
    etiqueta: 'Confirmado',
    simbolo: '✓',
    borde: 'border border-solid border-positivo-anillo',
  },
  pendiente: {
    clave: 'pendiente',
    etiqueta: 'Pendiente',
    simbolo: '⏳',
    borde: 'border border-dotted border-pendiente-anillo',
  },
  aplazado: {
    clave: 'aplazado',
    etiqueta: 'Aplazado',
    simbolo: '»',
    borde: 'border border-dashed border-atencion-anillo',
  },
}

/** El estado por defecto cuando el reparto no trae uno: pendiente (aún no cerrado). */
export const ESTADO_POR_DEFECTO: EstadoReparto = 'pendiente'

/** La marca de un estado; si llega algo desconocido, cae a pendiente. */
export function marcaDeEstado(estado?: EstadoReparto | null): MarcaEstado {
  return MARCAS[estado ?? ESTADO_POR_DEFECTO] ?? MARCAS[ESTADO_POR_DEFECTO]
}

/** Los tres estados en orden de ciclo, para pintar la leyenda sin ordenar a mano. */
export const MARCAS_LISTA: readonly MarcaEstado[] = [
  MARCAS.confirmado,
  MARCAS.pendiente,
  MARCAS.aplazado,
]
